import { Suspense, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import {
  isContinuityTraversalPath,
  resolveTraversalStepFromPath,
} from "../lib/continuityTraversalNav";
import { ContinuityTraversalShell } from "./continuity/ContinuityTraversalRail";
import { ContinuityYouAreHere } from "./continuity/ContinuityYouAreHere";
import { XOCenterNav } from "./xo_center/XOCenterNav";
import { isXOCenterSurface, shouldAlignCenterNavTopRight } from "../lib/xo_center_visibility";
import { XO_INK, XO_SPACE, xoStampWash, xoStampWashSoft } from "./xoConvergenceBrand";
import { XOPublicQuietLoading } from "./XOPublicQuietLoading";
import { XOPublicRouteErrorBoundary } from "./XOPublicRouteErrorBoundary";
import "./continuity/ContinuityTraversalRail.css";

export const XO_PUBLIC_COLUMN_MAX_PX = 1120;

/** Narrow column for Signals drop — focal card + calmer secondary stack. */
export const XO_SIGNALS_COLUMN_MAX_PX = 720;

type XOPublicShellProps = {
  children: ReactNode;
  /**
   * When true, children span the full shell width (no centered max-width column).
   * Outer ink + stamp washes stay the same — for long-form cinematic pages like `/pitch`.
   */
  fullBleed?: boolean;
  /** Hide traversal rail even on continuity paths */
  hideTraversalRail?: boolean;
  /** Hide you-are-here context block */
  hideYouAreHere?: boolean;
};

/**
 * Shared cinematic column for public routes: XO Ink, soft washes, centered max-width body.
 * Continuity traversal rail appears on primer / bottle / replay / horizon surfaces.
 */
export function XOPublicShell({
  children,
  fullBleed = false,
  hideTraversalRail = false,
  hideYouAreHere = false,
}: XOPublicShellProps) {
  const { pathname, search } = useLocation();
  const onTraversal = isContinuityTraversalPath(pathname);
  const currentStep = resolveTraversalStepFromPath(pathname, search);
  const showRail = onTraversal && !hideTraversalRail;
  const hostname =
    typeof globalThis !== "undefined" && "location" in globalThis
      ? (globalThis as unknown as { location: { hostname: string } }).location.hostname
      : "";
  const onCenter = isXOCenterSurface(hostname, pathname);
  const hubPath = pathname.replace(/\/$/, "") || "/";
  const showCenterNav = onCenter && hubPath !== "/" && hubPath !== "/xo-center";
  const navTopRight = showCenterNav && showRail && shouldAlignCenterNavTopRight(hostname, pathname);
  const showContext =
    onTraversal && !hideYouAreHere && pathname !== "/primer" && pathname !== "/primer/";

  const column = (
    <div
      style={{
        position: "relative",
        maxWidth: fullBleed ? "none" : XO_PUBLIC_COLUMN_MAX_PX,
        marginInline: "auto",
        width: "100%",
        minWidth: 0,
        minHeight: 0,
        overflowX: "hidden",
        padding: fullBleed
          ? showRail
            ? `${XO_SPACE.normal}px clamp(12px, 4vw, ${XO_SPACE.normal}px) ${XO_SPACE.section}px`
            : 0
          : `${XO_SPACE.normal}px clamp(12px, 4vw, ${XO_SPACE.normal}px) ${XO_SPACE.section}px`,
        boxSizing: "border-box",
      }}
    >
      {showCenterNav && !navTopRight ? <XOCenterNav /> : null}
      {showContext ? <ContinuityYouAreHere currentStep={currentStep} /> : null}
      {children}
    </div>
  );

  const body = showRail ? (
    <ContinuityTraversalShell currentStep={currentStep}>{column}</ContinuityTraversalShell>
  ) : (
    column
  );

  return (
    <div
      className="xo-public-shell xo-continuity-surface-enter"
      style={{
        position: "relative",
        width: "100%",
        flex: 1,
        minWidth: 0,
        minHeight: "100dvh",
        overflowX: "hidden",
        background: XO_INK,
        boxSizing: "border-box",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background: xoStampWash,
          pointerEvents: "none",
        }}
      />
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background: xoStampWashSoft,
          pointerEvents: "none",
        }}
      />
      <div style={{ position: "relative", minHeight: "100%" }}>
        {navTopRight ? (
          <XOCenterNav className="xo-center-nav--traversal-top-right" />
        ) : null}
        {body}
      </div>
    </div>
  );
}

export function XOPublicShellWithSuspense({
  children,
  loadingLabel = "Loading surface…",
  fullBleed = false,
  hideTraversalRail,
  hideYouAreHere,
}: {
  children: ReactNode;
  loadingLabel?: string;
  fullBleed?: boolean;
  hideTraversalRail?: boolean;
  hideYouAreHere?: boolean;
}) {
  return (
    <XOPublicShell
      fullBleed={fullBleed}
      hideTraversalRail={hideTraversalRail}
      hideYouAreHere={hideYouAreHere}
    >
      <XOPublicRouteErrorBoundary>
        <Suspense fallback={<XOPublicQuietLoading label={loadingLabel} />}>{children}</Suspense>
      </XOPublicRouteErrorBoundary>
    </XOPublicShell>
  );
}
